import { GroupDocument, LearningLogDocument, LearningLogPublication, PersonalDocument, PersonalPublication, PlanningDocument, ProblemDocument, ProblemPublication, SupportPublication } from "./document-types";
const kDocumentKindPattern = /^[a-z][a-zA-Z0-9]*$/;
/**
 * A document `kind` is used as a Firestore path segment (the canonical-pointer slot) as well as the registry
 * key, so a kind is restricted to a camelCase identifier: a lowercase letter followed by letters/digits, with
 * no separators or special characters. This matches the built-in document type strings (e.g.
 * "learningLogPublication") and keeps the value safe as a Firestore document id.
 */
export function isValidDocumentKind(value) {
    return kDocumentKindPattern.test(value);
}
const gDocumentKindInfoMap = {};
export function registerDocumentKind(kind, info) {
    if (!isValidDocumentKind(kind)) {
        throw new Error(`Document kind "${kind}" is not a valid identifier ` +
            `(must be camelCase letters/digits, with no separators or special characters)`);
    }
    if (gDocumentKindInfoMap[kind]) {
        throw new Error(`Document kind "${kind}" is already registered`);
    }
    gDocumentKindInfoMap[kind] = Object.assign({ kind }, info);
}
export function getDocumentKindInfo(kind) {
    return kind ? gDocumentKindInfoMap[kind] : undefined;
}
/** A kind's full stamp set (its metadataFields plus the `kind` key), or `{}` if the kind is unregistered. */
export function getDocumentKindMetadataFields(kind) {
    const info = getDocumentKindInfo(kind);
    if (!info)
        return {};
    return Object.assign({ kind: info.kind }, info.metadataFields);
}
export function getDocumentOwnerType(kind) {
    var _a, _b;
    return (_b = (_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.ownerType) !== null && _b !== void 0 ? _b : "user";
}
/**
 * The owner uid to stamp on a new document of the given kind
 */
export function getDocumentOwner(kind, ctx) {
    var _a, _b;
    switch (getDocumentOwnerType(kind)) {
        case "group": return (_a = ctx.groupOwnerId) !== null && _a !== void 0 ? _a : ctx.userId;
        case "class": return (_b = ctx.classOwnerId) !== null && _b !== void 0 ? _b : ctx.userId;
        case "user": return ctx.userId;
        default: return ctx.userId;
    }
}
/**
 * The scope fields to stamp on a document of the given kind, selected by its registered `scopeType`.
 */
export function getDocumentScopeFields(kind, ctx) {
    var _a;
    const scopeType = (_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.scopeType;
    switch (scopeType) {
        case "group": return {
            unit: ctx.unit,
            investigation: ctx.investigation,
            problem: ctx.problem,
            context_id: ctx.context_id,
            offeringId: ctx.offeringId,
            groupId: ctx.groupId
        };
        case "classUnit": return {
            unit: ctx.unit,
            context_id: ctx.context_id
        };
        case "class": return {
            unit: null,
            context_id: ctx.context_id
        };
        case "offering": return {
            unit: ctx.unit,
            investigation: ctx.investigation,
            problem: ctx.problem,
            context_id: ctx.context_id,
            offeringId: ctx.offeringId
        };
        default: return {
            unit: null,
            context_id: ctx.context_id
        };
    }
}
/**
 * The display title for a document based on its kind
 */
export function getDocumentTitle(document) {
    var _a;
    const registeredTitle = (_a = getDocumentKindInfo(document.kind)) === null || _a === void 0 ? void 0 : _a.title;
    if (registeredTitle != null)
        return registeredTitle;
    // Keyed on `type`, not `kind`: pre-existing group documents predate the `kind` axis and may have no stored
    // `kind` yet. We backfill the kind on open but we need the title for the lists of documents before they
    // are opened. Class-wide docs are new and always carry a `kind`, so their title is resolved above by kind.
    if (document.type === GroupDocument)
        return `Group ${document.groupId} Document`;
    return undefined;
}
/**
 * Register a kind declared by a unit's `classWideDocuments` configuration. Every class-wide collaborative
 * document has the same shape — concurrent, owned by the synthetic class owner, scoped to the class + unit —
 * so only the kind key and the authored title come from the configuration. The title is registered rather than
 * stored per document so it resolves live by kind (see getDocumentTitle). Throws like registerDocumentKind
 * when the kind is malformed or already registered.
 */
export function registerClassWideDocumentKind(kind, title) {
    registerDocumentKind(kind, {
        metadataFields: { concurrent: true },
        ownerType: "class",
        scopeType: "classUnit",
        title
    });
}
function registerBuiltInDocumentKinds() {
    registerDocumentKind(GroupDocument, {
        metadataFields: { concurrent: true },
        ownerType: "group",
        scopeType: "group"
    });
    const personalLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        scopeType: "class"
    };
    registerDocumentKind(PersonalDocument, personalLikeKindInfo);
    registerDocumentKind(LearningLogDocument, personalLikeKindInfo);
    registerDocumentKind(PersonalPublication, personalLikeKindInfo);
    registerDocumentKind(LearningLogPublication, personalLikeKindInfo);
    const problemLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        scopeType: "offering"
    };
    registerDocumentKind(PlanningDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemPublication, problemLikeKindInfo);
    registerDocumentKind(SupportPublication, problemLikeKindInfo);
}
registerBuiltInDocumentKinds();
// Test-only: reset the registry to just the built-in kinds. The registry is module-global mutable state and
// registerDocumentKind throws on duplicates, so a test that registers a kind must reset before another test
// registers the same kind. (Jest gives each test file its own module registry, so this is only needed within a
// file that registers the same kind in more than one test.)
export function resetDocumentKindRegistryForTests() {
    for (const kind of Object.keys(gDocumentKindInfoMap)) {
        delete gDocumentKindInfoMap[kind];
    }
    registerBuiltInDocumentKinds();
}
//# sourceMappingURL=data:application/json;base64,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